'use client';

import { useState } from 'react';

import { clsx } from 'clsx';
import { useSelectedLayoutSegments } from 'next/navigation';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { CampaignBanner } from '@/components/CampaignBanner';
import { ON_IDENTIFIER } from '@/modules/experiments/config';
import { useExperiments } from '@/modules/experiments/useExperiments';
import { type ProductBySlugExtendedResponse } from '@/modules/product/aliases';
import { useProductBySlugExtendedPDP } from '@/modules/product/hooks/useProductBySlugExtendedPDP';

const BOLD = /<b>(.*?)<\/b>/;

function Message({ message }: { message: string }) {
  return message
    .split(BOLD)
    .map((part, index) => (index % 2 ? <b key={index}>{part}</b> : part));
}

const STYLES: Record<string, string> = {
  SUCCESS: styles.success,
  DANGER: styles.danger,
  NEUTRAL: styles.neutral,
};

export function TickerTape({
  productExtended,
}: {
  productExtended?: ProductBySlugExtendedResponse;
}) {
  const t = useTranslations('common');
  const [isPaused, setIsPaused] = useState(false);
  const tickerTape = productExtended?.ticker_tape;
  const messages = tickerTape?.messages.filter(({ message }) => message) ?? [];

  if (!tickerTape || messages.length === 0) {
    return null;
  }

  return (
    <div
      data-campaign-ticker-tape
      role="button"
      tabIndex={0}
      aria-pressed={isPaused}
      aria-label={t('CampaignTickerTape.Pause', {
        messages: messages
          .map(({ message }) => message.split(BOLD).join(''))
          .join('. '),
      })}
      className={clsx(styles.ticker, isPaused && styles.paused)}
      onClick={() => setIsPaused(!isPaused)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          setIsPaused(!isPaused);
        }
      }}
    >
      <CampaignBanner
        alwaysVisible
        className={STYLES[tickerTape.style] ?? styles.neutral}
      >
        {messages.map(({ message }, index) => (
          <CampaignBanner.Label key={index} className={styles.label}>
            <span>
              <Message message={message} />
            </span>
            <span aria-hidden="true">|</span>
          </CampaignBanner.Label>
        ))}
      </CampaignBanner>
    </div>
  );
}

function BuyerPdpTickerTape() {
  const { ppx_flexible_campaigns_ui_web } = useExperiments([
    'ppx_flexible_campaigns_ui_web',
  ]);
  const { data } = useProductBySlugExtendedPDP();

  if (ppx_flexible_campaigns_ui_web !== ON_IDENTIFIER) {
    return null;
  }

  return <TickerTape productExtended={data} />;
}

export function CampaignTickerTape() {
  const [root, , group] = useSelectedLayoutSegments();

  if (root !== 'products' || group !== '(buyer)') {
    return null;
  }

  return <BuyerPdpTickerTape />;
}
